import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { CheckIcon } from 'assets/icons';
import { PrimaryButton, SecondaryButton } from 'components/forms/atoms/FormButtons';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import { useFeedback } from 'contexts/FeedbackContext';
import { useState } from 'react';
import { useActivateBusinessUnitPromptVersionMutation } from 'services/api/businessUnitPromptVersions.api';
import type { BusinessUnitPromptVersion } from 'services/types/businessUnitPromptVersion';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_CARD, SHADOW } from 'styles/tokens';

interface Props {
  businessUnitId: number;
  version: BusinessUnitPromptVersion | null;
  expectedActiveVersionId: number | null;
  onClose: () => void;
  onActivated: (version: BusinessUnitPromptVersion) => void;
}

const getErrorMessage = (error: unknown): string => {
  const data = (error as { data?: unknown })?.data;
  return typeof data === 'string' ? data : 'No fue posible activar la versión.';
};

const ActivateSavedPromptVersionDialog = ({
  businessUnitId,
  version,
  expectedActiveVersionId,
  onClose,
  onActivated,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activateVersion, { isLoading }] = useActivateBusinessUnitPromptVersionMutation();
  const { notifySaved } = useFeedback();

  const handleActivate = async () => {
    if (!version) return;
    setErrorMessage(null);
    try {
      const activated = await activateVersion({
        businessUnitId,
        versionId: version.id,
        expectedActiveVersionId,
      }).unwrap();
      notifySaved('Versión activada');
      onActivated(activated);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <Dialog
      open={Boolean(version)}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: RADIUS_CARD,
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: SHADOW,
            fontFamily: 'Jost, sans-serif',
          },
        },
      }}
    >
      <DialogTitle sx={{ color: NAVY, fontWeight: 700, px: 3, pt: 3 }}>Versión #{version?.id} guardada</DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1 }}>
        <Typography sx={{ color: NAVY }}>
          La versión quedó inactiva. ¿Quieres activarla ahora para usar este prompt y estas tools en producción?
        </Typography>
        {errorMessage && (
          <InlineFeedback severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </InlineFeedback>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <SecondaryButton onClick={onClose} disabled={isLoading}>
          Mantener inactiva
        </SecondaryButton>
        <PrimaryButton startIcon={<CheckIcon />} onClick={() => void handleActivate()} disabled={isLoading}>
          {isLoading ? 'Activando…' : 'Activar ahora'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ActivateSavedPromptVersionDialog;
